import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ButtonCustom } from "../auth/styles";
import { Trans, useTranslation } from "react-i18next";
import { UIInput } from "ui";

import "./RecoverPassword.sass";
import { ROUTE_URLS } from "../../constants/consts";
import Swal from "sweetalert2";
import { initRecoverPassword } from "../../api/userPasswordReset";

const USER_NOT_ACTIVE_ERROR = "The user is not active.";

const RecoverPassword: React.FC = () => {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [emailError, setEmailError] = useState("");
  const navigate = useNavigate();
  const optionsForAlertSuccess: any = {
    title: t("recover_password_form.check_email_title"),
    text: t("recover_password_form.check_email_message"),
    icon: "success",
    confirmButtonText: t("confirmButton"),
    confirmButtonColor: "#0ebfff",
  };

  const optionsForAlertWithInvalidCred: any = {
    title: t("recover_password_form.user_not_found_title"),
    text: t("recover_password_form.user_not_found_message"),
    icon: "error",
    confirmButtonText: t("confirmButton"),
    confirmButtonColor: "#0ebfff",
  };

  const optionsForAlertUserNotActive: any = {
    title: t("recover_password_form.user_not_active_title"),
    text: t("recover_password_form.user_not_active_message"),
    icon: "warning",
    confirmButtonText: t("confirmButton"),
    confirmButtonColor: "#0ebfff",
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isLoading) {
      return;
    }

    setSubmitAttempted(true);
    const normalizedEmail = code.trim();
    if (!normalizedEmail) {
      setEmailError(t("required_field"));
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setEmailError(t("order_delivery_info.invalid_email"));
      return;
    }

    setEmailError("");
    setIsLoading(true);
    try {
      const data = {
        email: normalizedEmail,
      };
      const response = await initRecoverPassword(JSON.stringify(data));

      if (response.data.data.error === USER_NOT_ACTIVE_ERROR) {
        Swal.fire(optionsForAlertUserNotActive).then(() => {});
        navigate(ROUTE_URLS.SIGN_IN);
      } else if (response.data.data.error && response.data.data.error !== USER_NOT_ACTIVE_ERROR) {
        Swal.fire(optionsForAlertWithInvalidCred).then(() => {});
      } else {
        navigate(ROUTE_URLS.SIGN_IN);
        Swal.fire(optionsForAlertSuccess).then(() => {});
      }
    } catch (error) {
      Swal.fire(optionsForAlertWithInvalidCred).then(() => {});
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!submitAttempted) {
      return;
    }

    const normalizedEmail = code.trim();
    if (!normalizedEmail) {
      setEmailError(t("required_field"));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setEmailError(t("order_delivery_info.invalid_email"));
      return;
    }

    setEmailError("");
  }, [code, submitAttempted, t]);

  const textFieldStyles = {
    transition: "width 0.3s ease-in-out",
    width: "100%",
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: "#ebebeb",
      padding: "0 10px",
      "& fieldset": {
        borderColor: "transparent",
      },
      "&:hover fieldset": {
        border: "none",
        borderColor: "gray",
      },
      "&.Mui-focused fieldset": {
        border: "none",
      },
    },
  };

  const inputPropsStyles = {
    "&::placeholder": {
      color: "#757575",
      fontWeight: "bold",
    },
    "& .MuiInputBase-input:-webkit-autofill": {
      WebkitBoxShadow: "0 0 0 1000px #ebebeb inset",
      WebkitTextFillColor: "#000000",
      transition: "background-color 5000s ease-in-out 0s",
    },
    "& .MuiInputBase-input": {
      color: "#000000",
      fontWeight: "bold",
    },
  };

  const inputLabelPropsStyles = {
    color: "#757575",
    fontWeight: "bold",
    "&.Mui-focused": {
      color: "#757575",
    },
  };

  return (
    <div>
      <form className={"recover_form"} onSubmit={handleSubmit} noValidate>
        <p className={"help_text"}>
          <Trans i18nKey="recover_password_form.recovery_instructions" />
        </p>
        <UIInput
          value={code}
          required
          type="email"
          className={`recover_input ${emailError ? "recover_input--error" : ""}`}
          label={t("recover_password_form.email_label")}
          onChange={(e) => setCode(e.target.value || "")}
          InputProps={{
            sx: inputPropsStyles,
          }}
          InputLabelProps={{
            sx: inputLabelPropsStyles,
          }}
          sx={textFieldStyles}
          autoComplete="off"
          style={{ marginBottom: emailError ? "4px" : "15px" }}
        />
        {emailError ? <p className="recover_error">{emailError}</p> : null}
        <ButtonCustom
          type="submit"
          disabled={isLoading || (submitAttempted && Boolean(emailError))}
        >
          <Trans i18nKey="recover_password_form.send_button" />
        </ButtonCustom>
        <div className={"go_home_block"}>
          <Link to="/">
            <Trans i18nKey="go_to_home" />
          </Link>
        </div>
      </form>
    </div>
  );
};

export default RecoverPassword;
